/**
 * Product analytics — the funnel in docs/marketing/metrics.md, and nothing else.
 *
 * Umami is cookieless and stores no personal data, which is why it was chosen:
 * no consent banner stands between a visitor and the docs. Keep it that way.
 * Never pass an email, an API key, a series, or anything else that identifies a
 * person or their data — event names and small enumerable properties only.
 *
 * The script tag is injected at build time by scripts/vite-plugin-analytics.ts,
 * and only when both env vars are set. Everywhere else — dev, a preview build, a
 * deploy that has not configured Umami — `window.umami` is absent and every call
 * here is a no-op, so call sites never have to check.
 */

/**
 * The funnel, as defined in docs/marketing/metrics.md. A union rather than a
 * string so a typo fails the build instead of quietly creating a second event
 * that splits a funnel in half.
 *
 * `page_view` is deliberately absent: Umami records those itself, including
 * client-side route changes.
 */
export type AnalyticsEvent =
  | 'signup_started'
  | 'signup_completed'
  | 'playground_opened'
  | 'playground_forecast'
  | 'key_created'
  | 'docs_viewed'
  | 'integration_viewed'
  | 'article_viewed'
  | 'pricing_viewed'
  | 'upgrade_started'
  | 'upgrade_completed';

/** Values Umami accepts as event data. */
type EventProps = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    umami?: { track: (event: string, data?: EventProps) => void };
  }
}

/**
 * Record one funnel event. Never throws: analytics failing must not take a
 * signup with it, and an ad blocker removing the script is the common case
 * rather than an error.
 */
export function track(event: AnalyticsEvent, props?: EventProps): void {
  try {
    window.umami?.track(event, props);
  } catch {
    /* An analytics call is never worth an exception on a user's path. */
  }
}
